import React, { useState } from "react";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import CompleteProfileDialog from "./CompleteProfileDialog";

/**
 * LockedToolOverlay
 *
 * Wrappa una card di strumento AI. Se ``locked`` è false → renderizza
 * normalmente i children. Se true → mostra i children con un overlay
 * semi-trasparente + lucchetto + CTA "Sblocca con 1 minuto".
 *
 * Pensato per essere il MENO invasivo possibile: rimane visibile cosa
 * fa lo strumento (preview/anteprima), così l'utente è motivato a
 * sbloccarlo. Non sostituisce o nasconde la card.
 *
 * Props:
 *   - locked: bool (se false, l'overlay non viene renderizzato)
 *   - missing: array di stringhe leggibili dei campi mancanti
 *     (es. ["nome del salone", "posizionamento"])
 *   - focusField: il campo che il dialog deve auto-focare (opzionale)
 *   - children: la card dello strumento
 */
export default function LockedToolOverlay({ locked, missing = [], focusField, children }) {
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!locked) return children;

  const missingLabel = missing.length > 0
    ? missing.join(" + ")
    : "alcuni campi del profilo";

  return (
    <>
      <div className="relative" data-testid="locked-tool-wrapper">
        {/* Children con opacity ridotta + pointer-events disabilitati */}
        <div className="opacity-40 pointer-events-none select-none filter blur-[1px]">
          {children}
        </div>

        {/* Overlay sopra */}
        <div
          data-testid="locked-tool-overlay"
          className="absolute inset-0 rounded-3xl bg-white/30 backdrop-blur-[2px] border-2 border-dashed border-[var(--mp-ink)] flex flex-col items-center justify-center text-center p-6"
        >
          <div className="h-14 w-14 rounded-full bg-[var(--mp-yellow)] grid place-items-center mb-3 shadow-brutal-sm">
            <Lock className="h-6 w-6 text-[var(--mp-ink)]" />
          </div>
          <div className="font-display text-lg sm:text-xl tracking-tight max-w-xs">
            Strumento bloccato
          </div>
          <div className="text-[12.5px] text-[var(--mp-ink)] mt-1.5 max-w-sm">
            Ti serve <strong>{missingLabel}</strong> per usarlo.
          </div>
          <Button
            onClick={() => setDialogOpen(true)}
            data-testid="locked-tool-unlock-btn"
            className="mt-4 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white h-10 px-5"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Sblocca in 1 minuto
          </Button>
        </div>
      </div>

      <CompleteProfileDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        focusField={focusField}
      />
    </>
  );
}
